import React, { useState, useEffect } from 'react';
import { useSponsorshipApi } from '../../../Hooks/useSponsorshipApi';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Textarea } from '../../../ui/textarea';

/**
 * CategoryForm Modal Component (shadcn/ui version)
 * Handles create and edit operations for categories
 */
const CategoryFormShadcn = ({ category, onClose, onSuccess }) => {
  const { categories: categoriesApi } = useSponsorshipApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true
  });

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        description: category.description || '',
        is_active: category.is_active !== undefined ? category.is_active : true
      });
    }
  }, [category]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (category) {
        await categoriesApi.update(category.id || category._id, formData);
      } else {
        await categoriesApi.create(formData);
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-lg border bg-background shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold">
            {category ? 'Edit Category' : 'Create New Category'}
          </h2>
          <Button
            type="button"
            variant="ghost"
            className="h-8 w-8 p-0 text-xl"
            onClick={onClose}
          >
            ×
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 px-6 py-4">
          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">
                Category Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="e.g., Technology, Healthcare"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                placeholder="Brief description of the category"
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                id="is-active"
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => handleInputChange('is_active', e.target.checked)}
                className="h-4 w-4 rounded border-input"
              />
              <Label htmlFor="is-active" className="cursor-pointer">
                Active Category
              </Label>
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t bg-muted/40 px-6 py-4 -mx-6 -mb-4">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : (category ? 'Update Category' : 'Create Category')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryFormShadcn;
